import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import { API_ENDPOINTS } from '../config/api';
import { getViewMode, storeViewMode } from '../config/viewMode';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Token kept in memory only — never written to localStorage.
  // The httpOnly cookie (set by the server on login) handles persistence
  // across page reloads. XSS cannot access httpOnly cookies.
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewModeState] = useState(getViewMode);

  // On mount, restore session via the httpOnly cookie.
  // The browser sends the cookie automatically; if valid the server
  // returns the current user with no stored token needed.
  // Migrate any legacy localStorage token: send it once then remove it.
  useEffect(() => {
    const legacyToken = (() => { try { return localStorage.getItem('terraMindToken'); } catch (_) { return null; } })();
    const headers = legacyToken ? { Authorization: `Token ${legacyToken}` } : {};

    axios.get(API_ENDPOINTS.AUTH_ME, { headers })
      .then((res) => {
        setUser(res.data);
        if (legacyToken) {
          setToken(legacyToken);
          // Clean up legacy storage now that the cookie is set
          try { localStorage.removeItem('terraMindToken'); } catch (_) { /* private mode */ }
        }
      })
      .catch(() => {
        setUser(null);
        setToken(null);
        try { localStorage.removeItem('terraMindToken'); } catch (_) { /* private mode */ }
      })
      .finally(() => setLoading(false));
  }, []); // run once on mount

  const login = useCallback(async (username, password) => {
    const res = await axios.post(API_ENDPOINTS.AUTH_LOGIN, { username, password });
    const { token: newToken, user: userData } = res.data;
    // Keep token in React state for the current session's Authorization header.
    // The server also sets an httpOnly cookie — page reloads will use that.
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(API_ENDPOINTS.AUTH_LOGOUT);
    } catch (_) { /* ignore */ }
    setToken(null);
    setUser(null);
    // Server clears the httpOnly cookie in logout_view.
  }, []);

  // Capability check against the entitlements the server resolved (S-B2).
  // Defensive: an older payload (or the brief pre-/me window) may lack the
  // array — treat a missing capability as "not granted" rather than throwing.
  const hasEntitlement = useCallback(
    (capability) => Array.isArray(user?.entitlements) && user.entitlements.includes(capability),
    [user],
  );

  // Switching view reloads the page, so every list is fetched again under the
  // new mode rather than keeping rows loaded in the other one.
  const setViewMode = useCallback((mode) => {
    storeViewMode(mode);
    setViewModeState(mode === 'admin' ? 'admin' : 'farmer');
    window.location.reload();
  }, []);

  const hasAdminRole = user?.role === 'admin';
  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    // The admin tools show only in the admin view; the role alone shows the switch.
    isAdmin: hasAdminRole && viewMode === 'admin',
    hasAdminRole,
    viewMode,
    setViewMode,
    isFarmer: user?.role === 'farmer',
    hasEntitlement,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
